import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  X,
  Loader2,
  ArrowUpRight,
  CheckCircle2,
  Bell,
  BellRing,
} from "lucide-react";
import { toast } from "sonner";
import { saveSearch } from "@/lib/api";
import { SAVED } from "@/constants/testIds";

const EASE = [0.76, 0, 0.24, 1];
const LS_KEY = "orphion_saved_email";

const formatDetail = (d) => {
  if (!d) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" · ");
  return String(d);
};

const summariseFilters = (f) => {
  const parts = [];
  if (f.industry?.length) parts.push(f.industry.join(", "));
  if (f.tech?.length) parts.push(f.tech.join(" · "));
  if (f.seniority?.length) parts.push(f.seniority.join(" / "));
  if (f.remote?.length) parts.push(f.remote.join(" / "));
  if (f.search) parts.push(`“${f.search}”`);
  return parts.join(" — ") || "All roles";
};

export const SaveSearchModal = ({ open, onClose, filters }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [label, setLabel] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      const saved = localStorage.getItem(LS_KEY) || "";
      setEmail(saved);
      setSuccess(false);
      setError("");
      // Suggest a label from filters
      setLabel(summariseFilters(filters).slice(0, 100));
    }
  }, [open, filters]);

  const reset = () => {
    setName("");
    setEmail("");
    setLabel("");
    setLoading(false);
    setError("");
    setSuccess(false);
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const noFilters =
    !(filters.industry?.length || filters.tech?.length || filters.seniority?.length ||
      filters.remote?.length || filters.search);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await saveSearch({
        name: name || null,
        email,
        label: label || null,
        filters: {
          industry: filters.industry || [],
          tech: filters.tech || [],
          seniority: filters.seniority || [],
          remote: filters.remote || [],
          search: filters.search || null,
        },
        alerts_enabled: true,
      });
      localStorage.setItem(LS_KEY, email);
      setSuccess(true);
      toast.success("Saved — we'll email you when a match goes live.");
      setTimeout(() => handleClose(), 2000);
    } catch (err) {
      setError(formatDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[75] bg-[rgba(20,17,13,0.55)] backdrop-blur-sm flex items-start md:items-center justify-center px-4 py-16 overflow-y-auto"
          onClick={handleClose}
          data-testid={SAVED.modal}
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="relative bg-[var(--bg)] border border-[var(--line-strong)] w-full max-w-lg p-8 md:p-10"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleClose}
              data-testid={SAVED.close}
              className="absolute top-4 right-4 text-[var(--ink-70)] hover:text-[var(--ink)] p-1"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 mb-3">
              <BellRing className="w-5 h-5 text-[var(--accent)]" strokeWidth={1.7} />
              <p className="overline">Save this search</p>
            </div>
            <h2 className="font-display text-3xl md:text-4xl tracking-[-0.02em] leading-[1.05] text-[var(--ink)] mb-2">
              Get pinged the{" "}
              <span className="font-serif-accent italic text-[var(--accent)]">
                moment
              </span>{" "}
              a match opens.
            </h2>
            <p className="text-[var(--ink-70)] text-sm mb-6">
              We&apos;ll email you as soon as a new role fits{" "}
              <span className="text-[var(--ink)] font-medium">
                {summariseFilters(filters)}
              </span>
              .
            </p>

            <form onSubmit={onSubmit} className="space-y-2">
              <div>
                <label className="overline block mb-2">Email</label>
                <input
                  required
                  type="email"
                  className="editorial-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  data-testid={SAVED.email}
                />
              </div>
              <div className="pt-4">
                <label className="overline block mb-2">Name (optional)</label>
                <input
                  className="editorial-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Priya"
                  data-testid={SAVED.name}
                />
              </div>
              <div className="pt-4">
                <label className="overline block mb-2">Label</label>
                <input
                  className="editorial-input"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g. Senior Python roles"
                  data-testid={SAVED.label}
                />
              </div>

              {noFilters && (
                <p className="pt-4 text-xs text-[var(--ink-70)]">
                  Heads up — no filters are set, so you&apos;ll be alerted about{" "}
                  <span className="text-[var(--accent)]">every</span> new role.
                </p>
              )}

              {error && (
                <div className="text-red-600 text-sm pt-4" data-testid={SAVED.error}>
                  {error}
                </div>
              )}
              {success && (
                <div
                  className="flex items-center gap-2 text-[var(--accent)] pt-4"
                  data-testid={SAVED.success}
                >
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="text-sm uppercase tracking-[0.22em]">
                    Saved · confirmation on its way
                  </span>
                </div>
              )}

              <div className="pt-8 flex items-center gap-4">
                <button
                  type="submit"
                  disabled={loading || success}
                  className="btn-pill disabled:opacity-60"
                  data-testid={SAVED.submit}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Saving…</span>
                    </>
                  ) : (
                    <>
                      <Bell className="w-4 h-4" />
                      <span>Save & alert me</span>
                      <ArrowUpRight className="w-4 h-4" />
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="text-sm uppercase tracking-[0.22em] text-[var(--ink-70)] hover:text-[var(--ink)] link-slide"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SaveSearchModal;
